import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Building2, Loader2, RefreshCw, Trash2 } from "lucide-react";
import {
  deleteCurrentCustomer,
  fetchCurrentCustomerDeletionCheck,
  updateCurrentCustomer,
  type CurrentCustomerDeletionCheckResponse,
  type OrganizationDeleteBlocker,
} from "@/api/auth";
import { ApiError, apiErrorMessage } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  normalizePostalCodeInput,
  POSTAL_CODE_PATTERN,
  usePostalCodeAddressAutofill,
  type PostalCodeLookupState,
} from "./postalCodeAddressLookup";

export function OrganizationSettingsPage() {
  const { t } = useTranslation();
  const { user, refreshMe } = useAuth();
  const navigate = useNavigate();
  const currentCustomer = useMemo(
    () =>
      user?.memberships.find(
        (membership) =>
          membership.customerId === user.currentMembership?.customerId,
      ) ?? null,
    [user?.currentMembership?.customerId, user?.memberships],
  );
  const isAdmin = user?.currentMembership?.role === "customer_admin";
  const [customerName, setCustomerName] = useState(
    currentCustomer?.customerName ?? "",
  );
  const [organizationPhoneNumber, setOrganizationPhoneNumber] = useState(
    currentCustomer?.organizationPhoneNumber ?? "",
  );
  const [postalCode, setPostalCode] = useState(currentCustomer?.postalCode ?? "");
  const [postalCodeLookupEnabled, setPostalCodeLookupEnabled] = useState(false);
  const [addressLine1, setAddressLine1] = useState(
    currentCustomer?.addressLine1 ?? "",
  );
  const [addressLine2, setAddressLine2] = useState(
    currentCustomer?.addressLine2 ?? "",
  );
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved">("idle");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deletionCheck, setDeletionCheck] =
    useState<CurrentCustomerDeletionCheckResponse | null>(null);
  const [isCheckingDeletion, setIsCheckingDeletion] = useState(false);
  const [deletionCheckError, setDeletionCheckError] = useState<string | null>(null);
  const [confirmationName, setConfirmationName] = useState("");
  const [confirmLocalGatewayRoutesDelete, setConfirmLocalGatewayRoutesDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const postalCodeLookupState = usePostalCodeAddressAutofill({
    postalCode,
    addressLine1,
    enabled: postalCodeLookupEnabled,
    setAddressLine1,
  });

  useEffect(() => {
    setCustomerName(currentCustomer?.customerName ?? "");
    setOrganizationPhoneNumber(currentCustomer?.organizationPhoneNumber ?? "");
    setPostalCode(currentCustomer?.postalCode ?? "");
    setPostalCodeLookupEnabled(false);
    setAddressLine1(currentCustomer?.addressLine1 ?? "");
    setAddressLine2(currentCustomer?.addressLine2 ?? "");
  }, [
    currentCustomer?.addressLine1,
    currentCustomer?.addressLine2,
    currentCustomer?.customerName,
    currentCustomer?.organizationPhoneNumber,
    currentCustomer?.postalCode,
  ]);

  const loadDeletionCheck = async () => {
    if (!isAdmin) return;
    setConfirmationName("");
    setConfirmLocalGatewayRoutesDelete(false);
    setIsCheckingDeletion(true);
    setDeletionCheckError(null);
    try {
      setDeletionCheck(await fetchCurrentCustomerDeletionCheck());
    } catch (error) {
      setDeletionCheckError(
        apiErrorMessage(
          error,
          t("organizationSettings.delete.checkFailed"),
        ),
      );
    } finally {
      setIsCheckingDeletion(false);
    }
  };

  useEffect(() => {
    void loadDeletionCheck();
  }, [isAdmin, user?.currentMembership?.customerId]);

  const handleSave = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextName = customerName.trim();
    const nextPhoneNumber = organizationPhoneNumber.trim();
    const nextPostalCode = postalCode.trim();
    const nextAddressLine1 = addressLine1.trim();
    const nextAddressLine2 = addressLine2.trim();
    if (!nextName) {
      setSaveError(t("organizationSettings.profile.nameRequired"));
      return;
    }
    if (!/^[0-9]{10,11}$/.test(nextPhoneNumber)) {
      setSaveError(t("organizationSettings.profile.phoneInvalid"));
      return;
    }
    if (!POSTAL_CODE_PATTERN.test(nextPostalCode)) {
      setSaveError(t("organizationSettings.profile.postalCodeInvalid"));
      return;
    }
    if (!nextAddressLine1) {
      setSaveError(t("organizationSettings.profile.addressRequired"));
      return;
    }
    setSaveState("saving");
    setSaveError(null);
    try {
      await updateCurrentCustomer({
        customerName: nextName,
        organizationPhoneNumber: nextPhoneNumber,
        postalCode: nextPostalCode,
        addressLine1: nextAddressLine1,
        addressLine2: nextAddressLine2 || null,
      });
      await refreshMe();
      setCustomerName(nextName);
      setOrganizationPhoneNumber(nextPhoneNumber);
      setPostalCode(nextPostalCode);
      setAddressLine1(nextAddressLine1);
      setAddressLine2(nextAddressLine2);
      setSaveState("saved");
    } catch (error) {
      setSaveError(
        apiErrorMessage(error, t("organizationSettings.profile.saveFailed")),
      );
      setSaveState("idle");
    }
  };

  const handleDelete = async () => {
    if (!deletionCheck || confirmationName !== deletionCheck.customerName || !confirmLocalGatewayRoutesDelete) {
      setDeleteError(t("organizationSettings.delete.confirmationMismatch"));
      return;
    }

    setIsDeleting(true);
    setDeleteError(null);
    try {
      await deleteCurrentCustomer({
        confirmationCustomerName: confirmationName,
        confirmLocalGatewayRoutesDelete,
      });
      await refreshMe();
      navigate("/console/dashboard", { replace: true });
    } catch (error) {
      if (
        error instanceof ApiError &&
        error.code === "ORGANIZATION_DELETE_BLOCKED"
      ) {
        await loadDeletionCheck();
      }
      setDeleteError(
        apiErrorMessage(error, t("organizationSettings.delete.deleteFailed")),
      );
    } finally {
      setIsDeleting(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-6">
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          {t("organizationSettings.forbidden")}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-md border bg-muted">
            <Building2 className="h-5 w-5 text-muted-foreground" />
          </span>
          <div>
            <h1 className="text-3xl font-bold">
              {t("organizationSettings.title")}
            </h1>
            <p className="text-sm text-muted-foreground">
              {currentCustomer?.customerName}
            </p>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t("organizationSettings.profile.title")}</CardTitle>
          <CardDescription>
            {t("organizationSettings.profile.description")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={handleSave}>
            <div className="space-y-2">
              <Label htmlFor="organizationName">
                {t("organizationSettings.profile.nameLabel")}
              </Label>
              <Input
                id="organizationName"
                value={customerName}
                maxLength={100}
                onChange={(event) => {
                  setCustomerName(event.target.value);
                  setSaveState("idle");
                }}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="organizationPhoneNumber">
                  {t("organizationSettings.profile.phoneLabel")}
                </Label>
                <Input
                  id="organizationPhoneNumber"
                  value={organizationPhoneNumber}
                  inputMode="numeric"
                  maxLength={11}
                  placeholder={t("organizationSettings.profile.phonePlaceholder")}
                  onChange={(event) => {
                    setOrganizationPhoneNumber(
                      event.target.value.replace(/\D/g, "").slice(0, 11),
                    );
                    setSaveState("idle");
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="organizationPostalCode">
                  {t("organizationSettings.profile.postalCodeLabel")}
                </Label>
                <Input
                  id="organizationPostalCode"
                  value={postalCode}
                  inputMode="numeric"
                  maxLength={7}
                  placeholder={t("organizationSettings.profile.postalCodePlaceholder")}
                  onChange={(event) => {
                    setPostalCode(normalizePostalCodeInput(event.target.value));
                    setPostalCodeLookupEnabled(true);
                    setSaveState("idle");
                  }}
                />
                <PostalCodeLookupMessage state={postalCodeLookupState} />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="organizationAddressLine1">
                {t("organizationSettings.profile.addressLine1Label")}
              </Label>
              <Input
                id="organizationAddressLine1"
                value={addressLine1}
                placeholder={t("organizationSettings.profile.addressLine1Placeholder")}
                onChange={(event) => {
                  setAddressLine1(event.target.value);
                  setSaveState("idle");
                }}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="organizationAddressLine2">
                {t("organizationSettings.profile.addressLine2Label")}
              </Label>
              <Input
                id="organizationAddressLine2"
                value={addressLine2}
                placeholder={t("organizationSettings.profile.addressLine2Placeholder")}
                onChange={(event) => {
                  setAddressLine2(event.target.value);
                  setSaveState("idle");
                }}
              />
            </div>
            <p className="text-sm text-muted-foreground">
              {t("organizationSettings.profile.boundaryNote")}
            </p>
            {saveError ? (
              <p className="text-sm font-medium text-destructive">{saveError}</p>
            ) : null}
            {saveState === "saved" ? (
              <p className="text-sm font-medium text-emerald-700">
                {t("organizationSettings.profile.saved")}
              </p>
            ) : null}
            <div className="flex justify-end">
              <Button type="submit" disabled={saveState === "saving"}>
                {saveState === "saving" ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                {t("actions.save")}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card className="border-destructive/30 bg-destructive/5">
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                {t("organizationSettings.delete.title")}
              </CardTitle>
              <CardDescription>
                {t("organizationSettings.delete.description")}
              </CardDescription>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void loadDeletionCheck()}
              disabled={isCheckingDeletion}
            >
              {isCheckingDeletion ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              {t("organizationSettings.delete.recheck")}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {deletionCheckError ? (
            <div className="rounded-md border border-destructive/30 bg-background p-3 text-sm text-destructive">
              {deletionCheckError}
            </div>
          ) : null}

          {deletionCheck ? (
            <DeletePreflightSummary
              blockers={deletionCheck.blockers}
              deletable={deletionCheck.deletable}
            />
          ) : null}

          <div className="rounded-md border bg-background p-4 text-sm text-muted-foreground">
            {t("organizationSettings.delete.scopeNote")}
          </div>

          <div className="space-y-2">
            <Label htmlFor="deleteConfirmation">
              {t("organizationSettings.delete.confirmationLabel", {
                name: deletionCheck?.customerName ?? currentCustomer?.customerName ?? "",
              })}
            </Label>
            <Input
              id="deleteConfirmation"
              value={confirmationName}
              onChange={(event) => setConfirmationName(event.target.value)}
              disabled={!deletionCheck?.deletable || isDeleting}
            />
          </div>

          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={confirmLocalGatewayRoutesDelete}
              onChange={(event) => setConfirmLocalGatewayRoutesDelete(event.target.checked)}
              disabled={!deletionCheck?.deletable || isDeleting}
            />
            {t("organizationSettings.delete.gatewayRoutesConfirmation")}
          </label>

          {deleteError ? (
            <p className="text-sm font-medium text-destructive">{deleteError}</p>
          ) : null}

          <div className="flex justify-end">
            <Button
              type="button"
              variant="destructive"
              disabled={
                isDeleting ||
                !deletionCheck?.deletable ||
                !confirmLocalGatewayRoutesDelete ||
                confirmationName !== deletionCheck.customerName
              }
              onClick={() => void handleDelete()}
            >
              {isDeleting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="mr-2 h-4 w-4" />
              )}
              {t("organizationSettings.delete.submit")}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function PostalCodeLookupMessage({ state }: { state: PostalCodeLookupState }) {
  const { t } = useTranslation();
  if (state === "idle") return null;
  const messageKey = {
    searching: "postalCodeLookup.searching",
    autofilled: "postalCodeLookup.autofilled",
    found: "postalCodeLookup.found",
    not_found: "postalCodeLookup.notFound",
    error: "postalCodeLookup.error",
  }[state];
  return (
    <p
      className={
        state === "not_found" || state === "error"
          ? "text-xs text-destructive"
          : "text-xs text-muted-foreground"
      }
    >
      {t(messageKey)}
    </p>
  );
}

function DeletePreflightSummary({
  blockers,
  deletable,
}: {
  blockers: OrganizationDeleteBlocker[];
  deletable: boolean;
}) {
  const { t } = useTranslation();

  if (deletable) {
    return (
      <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm font-medium text-emerald-800">
        {t("organizationSettings.delete.deletable")}
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm">
      <p className="font-semibold text-amber-950">
        {t("organizationSettings.delete.blocked")}
      </p>
      <ul className="space-y-2 text-amber-950">
        {blockers.map((blocker, index) => (
          <li key={`${blocker.code}-${index}`}>{blocker.message}</li>
        ))}
      </ul>
    </div>
  );
}
